/**
 * GpxPickerDrawer — unified GPX course picker for bike course prediction.
 *
 * Right-side drawer with three tabs:
 *   Upload     — drag-drop / browse .gpx + "Use Example File" button
 *   My Files   — user's saved GPX files (DataGrid + quick-filter, delete)
 *   Community  — community-shared GPX courses
 *
 * onSelect source shapes:
 *   { type: 'upload',    file: File }
 *   { type: 'myFile',    fileId: number, record: Object }
 *   { type: 'community', file: File, name: string }
 *   { type: 'example' }
 */

import { useState, useEffect, useRef } from 'react';
import {
  Drawer, Box, Typography, Tabs, Tab, Button, IconButton,
  CircularProgress, Chip, useTheme, useMediaQuery, Alert,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RouteIcon from '@mui/icons-material/Route';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { getUserGpxFiles, deleteUserFile } from '../api/client';
import CommunityGpxFiles from '../pages/CommunityGpxFiles';

function SearchToolbar() {
  return (
    <Box px={1} pt={0.5}>
      <GridToolbarQuickFilter debounceMs={200} fullWidth size="small" />
    </Box>
  );
}

export default function GpxPickerDrawer({ open, onClose, onSelect, units = 'metric' }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [tab, setTab] = useState(0);

  // My Files tab state
  const [myFiles, setMyFiles] = useState([]);
  const [myFilesLoading, setMyFilesLoading] = useState(false);
  const [myFilesLoaded, setMyFilesLoaded] = useState(false);
  const [myFilesError, setMyFilesError] = useState('');

  // Upload tab
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMyFilesLoaded(false);
      setMyFilesError('');
    }
  }, [open]);

  // Lazy-load My Files when tab is selected
  useEffect(() => {
    if (!open || tab !== 1 || myFilesLoaded || !user) return;
    (async () => {
      setMyFilesLoading(true);
      setMyFilesError('');
      try {
        const files = await getUserGpxFiles();
        setMyFiles(files);
        setMyFilesLoaded(true);
      } catch {
        setMyFilesError('Failed to load GPX files');
      } finally {
        setMyFilesLoading(false);
      }
    })();
  }, [open, tab, myFilesLoaded, user]);

  const handleFileChosen = (file) => {
    if (!file) return;
    onSelect({ type: 'upload', file });
    onClose();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChosen(file);
  };

  const handleMyFileDelete = async (id, e) => {
    e.stopPropagation();
    const record = myFiles.find((f) => f.id === id);
    if (!record || !window.confirm(`Delete "${record.filename}"?`)) return;
    try {
      await deleteUserFile('gpx', id);
      setMyFiles((prev) => prev.filter((f) => f.id !== id));
    } catch {
      alert('Failed to delete file');
    }
  };

  const handleExample = () => {
    onSelect({ type: 'example' });
    onClose();
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const myFilesColumns = [
    {
      field: 'filename',
      headerName: 'File',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[100]} noWrap title={params.row.filename}>
          {params.row.filename}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 90,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      field: 'total_distance',
      headerName: 'Dist',
      width: 80,
      valueFormatter: (v) => v != null
        ? (units === 'imperial' ? `${(v / 1609.34).toFixed(1)} mi` : `${(v / 1000).toFixed(1)} km`)
        : '-',
    },
    {
      field: 'terrain_type',
      headerName: 'Terrain',
      width: 90,
      renderCell: (params) => params.value
        ? <Chip
            label={params.value === 'paved' ? 'Paved' : params.value === 'gravel' ? 'Gravel' : 'Mixed'}
            size="small"
            sx={{ fontSize: '0.65rem', height: 18 }}
          />
        : null,
    },
    {
      field: 'location_name',
      headerName: '📍',
      width: 120,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.blueAccent[400]} noWrap title={params.value}>{params.value}</Typography>
        : null,
    },
    {
      field: '_delete',
      headerName: '',
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMyFileDelete(params.row.id, e)}
          sx={{ color: colors.redAccent[400] }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const gridSx = {
    height: '100%',
    border: 'none',
    fontSize: '0.8rem',
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '80%',
          backgroundColor: colors.grey[900],
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        sx={{ borderBottom: `1px solid ${colors.grey[700]}`, backgroundColor: colors.grey[800], flexShrink: 0 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <RouteIcon sx={{ color: colors.blueAccent[400] }} />
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
            Select GPX Course
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: colors.grey[300] }} />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: `1px solid ${colors.grey[700]}`,
          backgroundColor: colors.grey[800],
          flexShrink: 0,
          '& .MuiTab-root': { textTransform: 'none', color: colors.grey[400], minWidth: 90 },
          '& .Mui-selected': { color: colors.blueAccent[400] },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
        }}
      >
        <Tab label="Upload" />
        <Tab label="My Files" disabled={!user} />
        <Tab label="Community Courses" />
      </Tabs>

      {/* Tab content */}
      <Box flex={1} overflow="hidden" display="flex" flexDirection="column" p={tab === 1 ? 0 : 2}>

        {/* ── Upload tab ── */}
        {tab === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? colors.blueAccent[400] : colors.grey[600]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? 'rgba(33,150,243,0.08)' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: colors.blueAccent[500], backgroundColor: 'rgba(33,150,243,0.05)' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: colors.grey[500], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[300]} mb={0.5}>
                Drag & drop a GPX file here
              </Typography>
              <Typography variant="body2" color={colors.grey[500]}>
                or click to browse
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".gpx"
                hidden
                onChange={(e) => handleFileChosen(e.target.files?.[0])}
              />
            </Box>

            {/* Example file */}
            <Box
              sx={{
                border: `1px solid ${colors.grey[700]}`,
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight="bold" color={colors.grey[200]}>
                  Example: IM France Nice
                </Typography>
                <Typography variant="caption" color={colors.grey[500]}>
                  Full Ironman bike course · Nice, France · ~180 km
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExample}
                sx={{
                  textTransform: 'none',
                  color: colors.grey[200],
                  borderColor: colors.grey[600],
                  '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: 'rgba(33,150,243,0.08)' },
                }}
              >
                Use this file
              </Button>
            </Box>
          </Box>
        )}

        {/* ── My Files tab ── */}
        {tab === 1 && (
          <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
            {myFilesLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress size={32} />
              </Box>
            )}
            {myFilesError && <Alert severity="error" sx={{ m: 2 }}>{myFilesError}</Alert>}
            {!myFilesLoading && !myFilesError && myFiles.length === 0 && (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1}>
                <RouteIcon sx={{ fontSize: 52, color: colors.grey[600], mb: 1 }} />
                <Typography color={colors.grey[400]}>No saved GPX files yet</Typography>
                <Typography variant="body2" color={colors.grey[600]} mt={0.5}>
                  Upload a GPX file during analysis to save it here
                </Typography>
              </Box>
            )}
            {!myFilesLoading && !myFilesError && myFiles.length > 0 && (
              <DataGrid
                rows={myFiles}
                columns={myFilesColumns}
                density="compact"
                onRowClick={(params) => {
                  onSelect({ type: 'myFile', fileId: params.row.id, record: params.row });
                  onClose();
                }}
                slots={{ toolbar: SearchToolbar }}
                disableColumnMenu
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                pageSizeOptions={[25, 50, 100]}
                sx={gridSx}
              />
            )}
          </Box>
        )}

        {/* ── Community tab ── */}
        {tab === 2 && (
          <CommunityGpxFiles
            embedded
            onClose={onClose}
            onSelectFile={(file) => {
              onSelect({ type: 'community', file, name: file.name });
              onClose();
            }}
          />
        )}
      </Box>
    </Drawer>
  );
}
